import { defineStore } from 'pinia';
import { ResObject, Session, Document } from '@visma-real-estate-solutions/spring-editor-shared';
import { apiGet, ApiResult } from '../utils/apiWrapper';

interface UserSessionState {
    session: Session | null;
    resObjects: ResObject[];
    loading: boolean;
    error: string | null;
}

const useUserSessionStore = defineStore('userSession', {
    state: (): UserSessionState => ({
        session: null,
        resObjects: [],
        loading: false,
        error: null,
    }),

    getters: {
        // Session getters
        getSession: (state): Session | null => state.session,
        getUser: (state) => state.session?.user || null,
        getTopLevelUser: (state) => state.session?.topLevelUser || null,
        isImpersonating: (state): boolean => state.session?.impersonate || false,
        isLoggedIn: (state): boolean => !!state.session?.user,

        // ResObjects getters
        getResObjects: (state): ResObject[] => state.resObjects,
        getActiveResObjects: (state): ResObject[] => state.resObjects.filter((obj: ResObject) => obj.active),
        getResObjectById:
            (state) =>
            (id: number): ResObject | undefined =>
                state.resObjects.find((obj: ResObject) => obj.id === id),

        // Loading and error states
        isLoading: (state): boolean => state.loading,
        getError: (state): string | null => state.error,
        hasError: (state): boolean => !!state.error,
    },

    actions: {
        // Session actions
        async fetchSession(): Promise<ApiResult<Session>> {
            this.loading = true;
            this.error = null;

            try {
                const result = await apiGet<Session>('/api/user/session');

                result
                    .map((sessionData) => {
                        this.session = sessionData;
                        return sessionData;
                    })
                    .mapErr((err) => {
                        this.error = err.message;
                        console.error('Failed to fetch session:', err.message);
                    });

                return result;
            } catch (unexpectedError) {
                const errorMessage = 'An unexpected error occurred while fetching session';
                this.error = errorMessage;
                console.error('Unexpected error fetching session:', unexpectedError);
                throw unexpectedError;
            } finally {
                this.loading = false;
            }
        },

        // ResObjects actions - fetches resObjects for all users in the same customer
        async fetchResObjects(): Promise<ApiResult<ResObject[]>> {
            if (!this.session?.user) {
                this.error = 'No user session available';
                return Promise.reject(new Error('No user session available'));
            }

            this.loading = true;
            this.error = null;

            try {
                const result = await apiGet<ResObject[]>('/api/resObject/for-user');

                result
                    .map((resObjects) => {
                        this.resObjects = resObjects;
                        return resObjects;
                    })
                    .mapErr((err) => {
                        this.error = err.message;
                        console.error('Failed to fetch resObjects for customer:', err.message);
                    });

                return result;
            } catch (unexpectedError) {
                const errorMessage = 'An unexpected error occurred while fetching resObjects for customer';
                this.error = errorMessage;
                console.error('Unexpected error fetching resObjects for customer:', unexpectedError);
                throw unexpectedError;
            } finally {
                this.loading = false;
            }
        },

        // Documents actions
        async fetchDocumentsByResObjectId(resObjectId: number): Promise<ApiResult<Document[]>> {
            if (!this.session?.user) {
                this.error = 'No user session available';
                return Promise.reject(new Error('No user session available'));
            }

            try {
                const result = await apiGet<Document[]>(`/api/resObject/${resObjectId}/documents`);
                return result;
            } catch (unexpectedError) {
                const errorMessage = 'An unexpected error occurred while fetching documents';
                console.error('Unexpected error fetching documents:', unexpectedError);
                throw unexpectedError;
            }
        },

        // Combined actions
        async initializeSession(): Promise<void> {
            const sessionResult = await this.fetchSession();

            sessionResult.map(async () => {
                // If session was fetched successfully and user exists, fetch resObjects
                if (this.session?.user) {
                    await this.fetchResObjects();
                }
            });
        },

        // State management actions
        clearSession(): void {
            this.session = null;
            this.resObjects = [];
            this.error = null;
        },

        clearError(): void {
            this.error = null;
        },

        setLoading(loading: boolean): void {
            this.loading = loading;
        },
    },
});

export { useUserSessionStore };
